import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserCog, ShieldCheck, KeyRound } from "lucide-react";
import UserGstinAccessDialog from "@/components/users/UserGstinAccessDialog";

/**
 * User → Role assignment matrix. Admins pick which of the seeded roles each org member gets.
 * Backend: GET /users, POST /users/{id}/role
 */
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState({});
  const [gstinDialogUser, setGstinDialogUser] = useState(null);

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
    setUsers(u.data || []);
    setRoles(r.data.roles || []);
  };
  useEffect(() => { load(); }, []);

  const assign = async (userId, roleCode) => {
    setBusy(b => ({ ...b, [userId]: true }));
    try {
      await api.post(`/users/${userId}/role`, { role_code: roleCode });
      toast.success(`Role updated to ${roleCode}`);
      setUsers(us => us.map(x => x.id === userId ? { ...x, role_code: roleCode } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(b => ({ ...b, [userId]: false }));
    }
  };

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="user-management">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <UserCog className="w-3.5 h-3.5" /> Users &amp; Role Assignments
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Map each org member to one of the {roles.length} configured roles below. Permission changes take
            effect on the user's next login.
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase text-muted-foreground">
          {users.length} member{users.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full dense">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Assigned Role</th>
              <th className="text-center">GSTIN Access</th>
              <th className="text-right">Change to</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted-foreground py-8">
                No members yet — invite users to your organization first.
              </td></tr>
            )}
            {users.map(u => {
              const currentRole = roles.find(r => r.code === u.role_code);
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.full_name || "—"}</td>
                  <td className="text-xs font-mono text-muted-foreground">{u.email}</td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3 h-3 text-accent" />
                      <span className="text-xs">{currentRole?.name || u.role_code || "—"}</span>
                      {currentRole && (
                        <span className="text-[10px] font-mono text-muted-foreground">
                          · {currentRole.permissions?.length || 0} perms
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    <Button size="sm" variant="outline"
                              onClick={() => setGstinDialogUser(u)}
                              data-testid={`btn-gstin-access-${u.id}`}>
                      <KeyRound className="w-3 h-3 mr-1" /> Manage
                    </Button>
                  </td>
                  <td className="text-right">
                    <Select
                      value={u.role_code || ""}
                      onValueChange={(v) => assign(u.id, v)}
                      disabled={busy[u.id]}
                    >
                      <SelectTrigger className="rounded-sm h-8 text-xs w-56 ml-auto" data-testid={`role-picker-${u.id}`}>
                        <SelectValue placeholder="Assign role…" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(r => (
                          <SelectItem key={r.id} value={r.code} data-testid={`role-option-${r.code}`}>
                            <div className="flex items-center gap-2">
                              <span>{r.name}</span>
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {r.permissions?.length || 0}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <UserGstinAccessDialog
        user={gstinDialogUser}
        open={!!gstinDialogUser}
        onOpenChange={(v) => { if (!v) setGstinDialogUser(null); }}
      />
    </div>
  );
}
